import React, { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Datagrid as DatagridRA,
  DatagridProps as DatagridRAProps,
  Record as RaRecord,
} from 'react-admin';
import { makeStyles } from '@material-ui/core/styles';

import {
  SelectAllCheckbox,
  SelectAllRowsOptions,
} from 'components/SelectionToolkit';

const useStyles = makeStyles({
  header: {
    fontWeight: 'bold',
    '& .MuiCheckbox-root.select-all': {
      display: (props: { hidePageSelectAll: boolean }) =>
        props.hidePageSelectAll ? 'none' : 'block',
    },
  },
});

type DatagridProps<RecordType extends RaRecord = RaRecord> = {
  isHeaderSelectable?: boolean;
  /**
   * Replaces the page-level header checkbox with one selecting every row of
   * the current filter across pages; `true` takes the defaults.
   */
  selectAllRows?: boolean | SelectAllRowsOptions<RecordType>;
} & DatagridRAProps<RecordType>;

export const Datagrid = <RecordType extends RaRecord = RaRecord>({
  isHeaderSelectable = true,
  selectAllRows = false,
  ...props
}: DatagridProps<RecordType>) => {
  const classes = useStyles({
    hidePageSelectAll: !isHeaderSelectable || Boolean(selectAllRows),
  });

  const [table, setTable] = useState<HTMLTableElement | null>(null);
  const [selectAllCell, setSelectAllCell] = useState<HTMLElement | null>(null);

  // The grid only exposes its header through the DOM, so the cross-page
  // checkbox is portalled into the cell of the (hidden) page-level one to
  // share the row checkboxes' column. Re-checked every render: the table
  // mounts after the first load and the cell comes and goes with bulk actions.
  useLayoutEffect(() => {
    const cell = selectAllRows
      ? table?.querySelector('thead .select-all')?.closest('th, td') ?? null
      : null;
    if (cell !== selectAllCell) {
      setSelectAllCell(cell as HTMLElement | null);
    }
  });

  return (
    <>
      <DatagridRA
        // Only the opted-in grids pay for the ref-to-state hop that re-renders
        // the table once more on mount.
        ref={selectAllRows ? setTable : undefined}
        {...props}
        classes={{ headerCell: classes.header }}
      >
        {props.children}
      </DatagridRA>
      {selectAllCell &&
        createPortal(
          <SelectAllCheckbox<RecordType>
            {...(typeof selectAllRows === 'object' ? selectAllRows : {})}
          />,
          selectAllCell,
        )}
    </>
  );
};

export default Datagrid;
